import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ListX, Trash2 } from "lucide-react";
import {
    Alert,
    Button,
    Card,
    Dropdown,
    Form,
    ListGroup,
    Nav,
    Spinner,
    Table,
    Modal,
} from "react-bootstrap";
import { AppCheckbox } from "../components/form";
import CurrencySwitcher from "../components/CurrencySwitcher";

const API = "/api/process-plans";
const MAX_ROW_UNDO = 50;

const PROCESS_SUB_TABS = new Set(["planning", "supply", "cut", "sew", "dashboard"]);

const FORECAST_CACHE_TTL_MS = 60 * 60 * 1000;
const FORECAST_LS_KEY = "processPlans_forecast_v3";

function readForecastCache(periodKey) {
    try {
        const raw = localStorage.getItem(FORECAST_LS_KEY);
        if (!raw) {
            return null;
        }
        const all = JSON.parse(raw);
        if (!all || typeof all !== "object") {
            return null;
        }
        const rec = all[periodKey];
        if (!rec?.storedAt || rec.payload == null) {
            return null;
        }
        if (Date.now() - Number(rec.storedAt) > FORECAST_CACHE_TTL_MS) {
            return null;
        }
        return rec.payload;
    } catch {
        return null;
    }
}

function writeForecastCache(periodKey, payload) {
    try {
        const raw = localStorage.getItem(FORECAST_LS_KEY);
        const all = raw ? JSON.parse(raw) : {};
        if (!all || typeof all !== "object") {
            return;
        }
        all[periodKey] = { storedAt: Date.now(), payload };
        localStorage.setItem(FORECAST_LS_KEY, JSON.stringify(all));
    } catch {
        /* ignore quota / private mode */
    }
}

/** Відповідь GET /forecast — для кешу. */
function normalizeForecastPayload(d) {
    const x = d || {};
    return {
        periodKey: x.periodKey,
        periodStart: x.periodStart,
        periodEnd: x.periodEnd,
        days: x.days,
        runwayTargetDays: x.runwayTargetDays ?? 7,
        minSpeedPerDay: x.minSpeedPerDay ?? 0.05,
        rows: Array.isArray(x.rows) ? x.rows : [],
    };
}

function fmtNum(n) {
    if (n == null || Number.isNaN(Number(n))) {
        return "—";
    }
    return Number(n).toLocaleString("uk-UA", { maximumFractionDigits: 2 });
}

function fmtMoneyUAH(n) {
    if (n == null || Number.isNaN(Number(n))) {
        return "—";
    }
    const x = Number(n);
    if (!Number.isFinite(x)) {
        return "—";
    }
    return `${x.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₴`;
}

/** Суми в гривнях (з API / розрахунку) — показ у ₴ або $ за курсом продажу USD (як перемикач на Виробництві). */
function fmtMaterialMoneyFromUAH(uahAmount, displayCurrency, usdSellRate) {
    if (displayCurrency === "USD") {
        const uah = Number(uahAmount);
        if (!Number.isFinite(uah)) {
            return "—";
        }
        const r = Number(usdSellRate);
        if (!Number.isFinite(r) || r <= 0) {
            return "—";
        }
        const usd = uah / r;
        const num = usd.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return `$ ${num}`;
    }
    return fmtMoneyUAH(uahAmount);
}

function qtyFor(row, field) {
    const raw = row?.[field];
    const fallback = row?.planQty;
    const n = Number(raw ?? fallback ?? 0);
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
}

function normalizePlanRowQuantities(row) {
    const planQty = qtyFor(row, "planQty");
    return {
        ...row,
        planQty,
        sewQty: qtyFor({ ...row, planQty }, "sewQty"),
        cutQty: qtyFor({ ...row, planQty }, "cutQty"),
    };
}

/** Як на /materials/stock: спочатку priceUAH, інакше priceUSD × курс продажу USD. */
function effectiveMaterialUnitUAH(line, usdSellRate) {
    const rate = Number(usdSellRate);
    const hasRate = Number.isFinite(rate) && rate > 0;
    let uah = Number(line.priceUAH);
    if (!Number.isFinite(uah)) {
        uah = 0;
    }
    if (uah === 0 && hasRate) {
        const usd = Number(line.priceUSD);
        if (Number.isFinite(usd)) {
            uah = usd * rate;
        }
    }
    return Math.round(uah * 100) / 100;
}

/** Зведення матеріалів по всіх підтверджених планах (для закупівель). */
function mergeSupplyAggregateRows(plans) {
    const map = new Map();
    for (const p of plans || []) {
        for (const ln of p.materialLines || []) {
            const id = ln.materialId != null ? String(ln.materialId) : "";
            if (!id || id === "undefined") {
                continue;
            }
            if (!map.has(id)) {
                map.set(id, {
                    materialId: ln.materialId,
                    typeName: ln.typeName || "—",
                    color: ln.color || "—",
                    unit: ln.unit || "—",
                    stockQty: Number(ln.stockQty) || 0,
                    requiredQty: 0,
                    orderQtySum: 0,
                });
            }
            const a = map.get(id);
            a.requiredQty += Number(ln.requiredQty) || 0;
            a.orderQtySum += Number(ln.orderQty) || 0;
            a.stockQty = Number(ln.stockQty) || 0;
        }
    }
    const rows = [...map.values()].map((r) => {
        const recommendedBuy = Math.max(0, Math.round((r.requiredQty - r.stockQty) * 100) / 100);
        return { ...r, recommendedBuy };
    });
    rows.sort((a, b) =>
        `${a.typeName} ${a.color}`.localeCompare(`${b.typeName} ${b.color}`, "uk", { sensitivity: "base" })
    );
    return rows;
}

function daysLeftLabel(v) {
    if (v === null || v === undefined) {
        return "—";
    }
    if (typeof v === "number" && !Number.isFinite(v)) {
        return "—";
    }
    return fmtNum(v);
}

const FORECAST_SORT_STRING_KEYS = new Set(["sku", "name", "color", "size"]);

function getForecastSortValue(row, key) {
    switch (key) {
        case "sku":
            return row.sku || "";
        case "name":
            return row.name || "";
        case "color":
            return row.color || "";
        case "size":
            return row.size || "";
        case "sold":
            return Number(row.sold) || 0;
        case "stock":
            return Number(row.stock) || 0;
        case "speed":
            return Number(row.speed) || 0;
        case "daysLeft":
            return row.daysLeft != null && Number.isFinite(Number(row.daysLeft))
                ? Number(row.daysLeft)
                : Number.POSITIVE_INFINITY;
        case "suggestedQty":
            return Number(row.suggestedQty) || 0;
        case "planQty":
            return Number(row.planQty) || 0;
        case "sewQty":
            return qtyFor(row, "sewQty");
        case "cutQty":
            return qtyFor(row, "cutQty");
        default:
            return "";
    }
}

/** Індекси рядків у порядку сортування (стан draftRows не змінюється — коректні idx для updateRow). */
function sortForecastIndices(draftRows, { key, dir }) {
    const indices = draftRows.map((_, i) => i);
    const mult = dir === "asc" ? 1 : -1;
    const isString = FORECAST_SORT_STRING_KEYS.has(key);
    indices.sort((ia, ib) => {
        const a = draftRows[ia];
        const b = draftRows[ib];
        const va = getForecastSortValue(a, key);
        const vb = getForecastSortValue(b, key);
        if (isString) {
            const c = String(va).localeCompare(String(vb), "uk", { sensitivity: "base" });
            if (c !== 0) {
                return mult * c;
            }
            return ia - ib;
        }
        const na = Number(va);
        const nb = Number(vb);
        if (na !== nb) {
            return mult * (na - nb);
        }
        return ia - ib;
    });
    return indices;
}

/** Токени для фільтра таблиці прогнозу (пробіл, кома, крапка з комою). */
function forecastTableFilterTokens(s) {
    return String(s || "")
        .toLowerCase()
        .split(/[\s,;]+/)
        .map((t) => t.trim())
        .filter(Boolean);
}

/** Усі токени — як підрядки в об’єднаних полях рядка (AND). */
function forecastRowMatchesTableFilter(row, tokens) {
    if (tokens.length === 0) {
        return true;
    }
    const hay = [row.sku, row.name, row.color, row.size, row.type]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
    return tokens.every((t) => hay.includes(t));
}

/** Рядок прогнозу з варіанта складу (без продажів за період — швидкість 0). */
function buildForecastRowFromWarehousePick(v) {
    const stock = Number(v.stock) || 0;
    return {
        sku: v.sku,
        offerId: v.offerId,
        productId: v.productId,
        name: v.name,
        color: v.color || "",
        size: v.size || "",
        type: v.type || "",
        stock,
        sold: 0,
        speed: 0,
        daysLeft: stock < 0 ? 0 : null,
        suggestedQty: 0,
        planQty: 0,
        sewQty: 0,
        cutQty: 0,
        excluded: false,
        selected: true,
    };
}

function sumQtyForProduct(product, field) {
    let s = 0;
    for (const { colors } of product.types) {
        for (const { items } of colors) {
            for (const { row } of items) {
                s += qtyFor(row, field);
            }
        }
    }
    return s;
}

function sortGroupedPlanTreeCopy(grouped, { key, dir }) {
    const arr = [...grouped];
    const mult = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
        if (key === "name") {
            const c = String(a.name || "").localeCompare(String(b.name || ""), "uk", { sensitivity: "base" });
            if (c !== 0) {
                return mult * c;
            }
            return 0;
        }
        const qa = sumQtyForProduct(a, "cutQty");
        const qb = sumQtyForProduct(b, "cutQty");
        if (qa !== qb) {
            return mult * (qa - qb);
        }
        return String(a.name || "").localeCompare(String(b.name || ""), "uk", { sensitivity: "base" });
    });
    return arr;
}

function ForecastSortableTh({ colKey, sortState, onSort, className, style, children }) {
    const active = sortState.key === colKey;
    const arrow = !active ? "" : sortState.dir === "asc" ? " ▲" : " ▼";
    return (
        <th
            role="button"
            className={`${className || ""}${active ? " text-primary" : ""}`}
            style={{ cursor: "pointer", userSelect: "none", ...style }}
            onClick={() => onSort(colKey)}
            title="Сортування; повторний клік — зворотний порядок"
        >
            {children}
            <span className="text-nowrap">{arrow}</span>
        </th>
    );
}

/**
 * Дерево: товар → тип (поле type з CRM) → колір → рядки (розмір + idx).
 * Якщо для товару лише один «порожній» тип (—), рядок «Тип» у UI не показуємо.
 */
function groupForecastRowsByProductTypeColor(rows) {
    const productOrder = [];
    const byProduct = new Map();
    for (let idx = 0; idx < rows.length; idx++) {
        const row = rows[idx];
        const name = String(row.name || "—").trim() || "—";
        const pid = row.productId;
        const productKey =
            pid != null && Number.isFinite(Number(pid)) && Number(pid) > 0
                ? `pid:${Number(pid)}`
                : `name:${name}`;
        const typeLabel = String(row.type ?? "").trim() || "—";
        const color = String(row.color ?? "").trim() || "—";

        if (!byProduct.has(productKey)) {
            byProduct.set(productKey, {
                name,
                productId: pid,
                typeOrder: [],
                types: new Map(),
            });
            productOrder.push(productKey);
        }
        const block = byProduct.get(productKey);
        if (!block.types.has(typeLabel)) {
            block.types.set(typeLabel, { colorOrder: [], colors: new Map() });
            block.typeOrder.push(typeLabel);
        }
        const tBlock = block.types.get(typeLabel);
        if (!tBlock.colors.has(color)) {
            tBlock.colors.set(color, []);
            tBlock.colorOrder.push(color);
        }
        tBlock.colors.get(color).push({ idx, row });
    }
    return productOrder.map((pk) => {
        const b = byProduct.get(pk);
        return {
            productKey: pk,
            name: b.name,
            productId: b.productId,
            types: b.typeOrder.map((t) => {
                const tb = b.types.get(t);
                return {
                    type: t,
                    colors: tb.colorOrder.map((c) => ({
                        color: c,
                        items: tb.colors.get(c),
                    })),
                };
            }),
        };
    });
}

function CuttingCardsList({ cards, emptyText, showShortages = false }) {
    if (!cards.length) {
        return <Alert variant="secondary" className="mb-0">{emptyText}</Alert>;
    }
    return (
        <div className="d-flex flex-column gap-3 processes-cutting-cards">
            {cards.map((card) => (
                <Card className="processes-cutting-card" key={`${card.planId}-${card.cardKey || card._id}`}>
                    <Card.Header className="processes-cutting-card-header">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                            <div>
                                <div className="fw-semibold">
                                    {card.name} · розмір {card.size || "—"}
                                </div>
                                <div className="text-muted small">
                                    {card.planTitle} · {card.periodStart} — {card.periodEnd}
                                </div>
                            </div>
                            <span className="badge rounded-pill text-bg-primary processes-cutting-card-total">
                                {fmtNum(card.totalQty)} шт
                            </span>
                        </div>
                    </Card.Header>
                    <Card.Body className="processes-cutting-card-body">
                        <div className="processes-cutting-color-list">
                            {(card.colors || []).map((line) => (
                                <span key={line.color} className="processes-cutting-color-chip">
                                    <span className="processes-cutting-color-name">{line.color}</span>
                                    <span className="processes-cutting-color-qty">{fmtNum(line.qty)} шт</span>
                                </span>
                            ))}
                        </div>
                        {showShortages && card.shortageLines?.length ? (
                            <div className="processes-cutting-shortages">
                                <div className="fw-semibold mb-1">Не вистачає тканини / матеріалів:</div>
                                <ul className="mb-0">
                                    {card.shortageLines.map((line) => (
                                        <li key={String(line.materialId)}>
                                            {line.typeName} {line.color} — бракує {fmtNum(line.shortageQty)}{" "}
                                            {line.unit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        {card.warnings?.length ? (
                            <div className="processes-cutting-warnings">
                                <div className="fw-semibold mb-1">Є попередження техкарт:</div>
                                <ul className="mb-0">
                                    {card.warnings.map((w, idx) => (
                                        <li key={`${w.sku || w.productId || "warning"}-${idx}`}>{w.message}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
}

function ProductionWorkCardsList({ cards, emptyText, actionLabel, actionFrom, actionTo, actionName, onMove, busyId }) {
    if (!cards.length) {
        return <Alert variant="secondary" className="mb-0">{emptyText}</Alert>;
    }
    return (
        <div className="d-flex flex-column gap-3 processes-cutting-cards">
            {cards.map((card) => (
                <Card className="processes-cutting-card" key={card.cardKey}>
                    <Card.Header className="processes-cutting-card-header">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                            <div>
                                <div className="fw-semibold">
                                    {card.name} · розмір {card.size || "—"}
                                </div>
                                <div className="text-muted small">Партії з виробничого плану</div>
                            </div>
                            <span className="badge rounded-pill text-bg-primary processes-cutting-card-total">
                                {fmtNum(card.totalQty)} шт
                            </span>
                        </div>
                    </Card.Header>
                    <Card.Body className="processes-cutting-card-body">
                        <div className="d-flex flex-column gap-2">
                            {(card.colors || []).map((line) => {
                                const isBusy = busyId === String(line.batchId);
                                return (
                                    <div
                                        key={String(line.batchId)}
                                        className="d-flex flex-wrap align-items-center gap-2 justify-content-between border rounded px-2 py-2 bg-light"
                                    >
                                        <div>
                                            <span className="fw-semibold">{line.color}</span>
                                            <span className="text-muted small ms-2">
                                                SKU {line.sku} · {fmtNum(line.qty)} шт
                                            </span>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={isBusy}
                                            onClick={() =>
                                                onMove(line.batchId, actionFrom, actionTo, line.qty, actionName)
                                            }
                                        >
                                            {isBusy ? "…" : actionLabel}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
}

export default function ProcessesPage() {
    const navigate = useNavigate();
    const { subTab } = useParams();

    const mainTab = useMemo(() => {
        const k = String(subTab || "planning").toLowerCase();
        return PROCESS_SUB_TABS.has(k) ? k : "planning";
    }, [subTab]);

    useEffect(() => {
        const raw = subTab == null ? "" : String(subTab).toLowerCase();
        if (raw && !PROCESS_SUB_TABS.has(raw)) {
            navigate("/processes/planning", { replace: true });
        }
    }, [subTab, navigate]);

    const [period, setPeriod] = useState("last7");
    const periodRef = useRef(period);
    periodRef.current = period;
    const [forecastMeta, setForecastMeta] = useState(null);
    const [draftRows, setDraftRows] = useState([]);
    const [forecastLoading, setForecastLoading] = useState(false);
    const [forecastError, setForecastError] = useState("");

    const [plansList, setPlansList] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    const [currentPlan, setCurrentPlan] = useState(null);
    const [planLoading, setPlanLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [showTitleModal, setShowTitleModal] = useState(false);

    const [nonSewBusy, setNonSewBusy] = useState(false);

    const [materialsPreview, setMaterialsPreview] = useState({ lines: [], warnings: [], totalOrderUAH: 0 });
    const [processesCurrency, setProcessesCurrency] = useState("UAH");
    const [usdBuyRate, setUsdBuyRate] = useState(null);
    const [usdSellRate, setUsdSellRate] = useState(null);
    const [materialsPreviewLoading, setMaterialsPreviewLoading] = useState(false);
    const materialsPreviewDebounceRef = useRef(null);
    const materialsPreviewScheduleIdRef = useRef(0);

    const [supplyPlans, setSupplyPlans] = useState([]);
    const [supplyLoading, setSupplyLoading] = useState(false);
    const [supplySaveLoading, setSupplySaveLoading] = useState(false);
    const [confirmSupplyBusyId, setConfirmSupplyBusyId] = useState(null);
    const [cuttingCards, setCuttingCards] = useState([]);
    const [cuttingCardsLoading, setCuttingCardsLoading] = useState(false);
    const [productionDashboardRows, setProductionDashboardRows] = useState([]);
    const [productionDashboardLoading, setProductionDashboardLoading] = useState(false);
    const [productionCardsByStage, setProductionCardsByStage] = useState({});
    const [productionCardsLoading, setProductionCardsLoading] = useState(false);
    const [productionMoveBusyId, setProductionMoveBusyId] = useState(null);

    /** Повне знімання рядків перед «Вилучити» — для Ctrl+Z / Cmd+Z */
    const rowUndoStackRef = useRef([]);

    const clearRowUndo = useCallback(() => {
        rowUndoStackRef.current = [];
    }, []);

    const refreshPlansList = useCallback(async () => {
        setListLoading(true);
        try {
            const res = await axios.get(API, { withCredentials: true });
            setPlansList(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error(e);
        } finally {
            setListLoading(false);
        }
    }, []);

    const loadForecast = useCallback(
        async (opts = {}) => {
            const force = Boolean(opts.force);
            const skipPlanGuard = Boolean(opts.skipPlanGuard);
            const periodKey = periodRef.current;

            if (!skipPlanGuard && currentPlan?._id && !force) {
                return;
            }

            if (!force) {
                const cached = readForecastCache(periodKey);
                if (cached) {
                    const d = normalizeForecastPayload(cached);
                    setForecastError("");
                    setForecastMeta({
                        periodKey: d.periodKey,
                        periodStart: d.periodStart,
                        periodEnd: d.periodEnd,
                        days: d.days,
                        runwayTargetDays: d.runwayTargetDays,
                        minSpeedPerDay: d.minSpeedPerDay,
                    });
                    clearRowUndo();
                    setDraftRows(d.rows.map(normalizePlanRowQuantities));
                    return;
                }
            }

            setForecastLoading(true);
            setForecastError("");
            try {
                const res = await axios.get(`${API}/forecast`, {
                    params: { period: periodKey },
                    withCredentials: true,
                });
                const d = normalizeForecastPayload(res.data || {});
                writeForecastCache(periodKey, res.data || {});
                setForecastMeta({
                    periodKey: d.periodKey,
                    periodStart: d.periodStart,
                    periodEnd: d.periodEnd,
                    days: d.days,
                    runwayTargetDays: d.runwayTargetDays,
                    minSpeedPerDay: d.minSpeedPerDay,
                });
                clearRowUndo();
                setDraftRows(d.rows.map(normalizePlanRowQuantities));
            } catch (e) {
                setForecastError(e.response?.data?.error || e.message || "Помилка");
                setDraftRows([]);
                clearRowUndo();
            } finally {
                setForecastLoading(false);
            }
        },
        [clearRowUndo, currentPlan?._id]
    );

    useEffect(() => {
        void refreshPlansList();
    }, [refreshPlansList]);

    const loadSupplyPlans = useCallback(async () => {
        setSupplyLoading(true);
        try {
            const res = await axios.get(`${API}/for-supply`, { withCredentials: true });
            setSupplyPlans(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка завантаження «Забезпечення»");
            setSupplyPlans([]);
        } finally {
            setSupplyLoading(false);
        }
    }, []);

    const loadCuttingCards = useCallback(async (availability) => {
        setCuttingCardsLoading(true);
        try {
            const res = await axios.get(`${API}/cutting-cards`, {
                params: { availability },
                withCredentials: true,
            });
            setCuttingCards(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка завантаження карток крою");
            setCuttingCards([]);
        } finally {
            setCuttingCardsLoading(false);
        }
    }, []);

    const loadProductionDashboard = useCallback(async () => {
        setProductionDashboardLoading(true);
        try {
            const res = await axios.get(`${API}/production-dashboard`, { withCredentials: true });
            setProductionDashboardRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка завантаження виробничого дашборду");
            setProductionDashboardRows([]);
        } finally {
            setProductionDashboardLoading(false);
        }
    }, []);

    const loadProductionCardsForStages = useCallback(async (stages) => {
        setProductionCardsLoading(true);
        try {
            const entries = await Promise.all(
                stages.map(async (stage) => {
                    const res = await axios.get(`${API}/production-cards`, {
                        params: { stage },
                        withCredentials: true,
                    });
                    return [stage, Array.isArray(res.data?.cards) ? res.data.cards : []];
                })
            );
            setProductionCardsByStage(Object.fromEntries(entries));
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка завантаження виробничих карток");
            setProductionCardsByStage({});
        } finally {
            setProductionCardsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (mainTab === "supply") {
            void loadSupplyPlans();
            void loadCuttingCards("shortage");
        } else if (mainTab === "cut") {
            void loadProductionCardsForStages(["ready_for_cut", "cut_in_progress"]);
        } else if (mainTab === "sew") {
            void loadProductionCardsForStages(["cut_done", "sew_in_progress"]);
        } else if (mainTab === "dashboard") {
            void loadProductionDashboard();
        }
    }, [mainTab, loadSupplyPlans, loadCuttingCards, loadProductionCardsForStages, loadProductionDashboard]);

    useEffect(() => {
        if (mainTab !== "planning") {
            return undefined;
        }
        if (!currentPlan?._id) {
            void loadForecast({});
        }
        return undefined;
    }, [mainTab, currentPlan?._id, loadForecast]);

    const openPlan = async (id) => {
        setPlanLoading(true);
        setForecastError("");
        try {
            const res = await axios.get(`${API}/${id}`, { withCredentials: true });
            const p = res.data;
            if (p.supplyConfirmed) {
                setPlanLoading(false);
                navigate("/processes/supply");
                void loadSupplyPlans();
                return;
            }
            setCurrentPlan(p);
            setPeriod(p.periodKey || "last7");
            setForecastMeta({
                periodKey: p.periodKey,
                periodStart: p.periodStart,
                periodEnd: p.periodEnd,
                days: null,
                runwayTargetDays: 7,
                minSpeedPerDay: 0.05,
            });
            clearRowUndo();
            setDraftRows(Array.isArray(p.forecastRows) ? p.forecastRows.map(normalizePlanRowQuantities) : []);
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка");
        } finally {
            setPlanLoading(false);
        }
    };

    const updateRow = (idx, patch) => {
        setDraftRows((prev) => {
            const next = [...prev];
            if (next[idx]) {
                next[idx] = { ...next[idx], ...patch };
            }
            return next;
        });
    };

    const removeRow = (idx) => {
        setDraftRows((prev) => {
            const snap = JSON.parse(JSON.stringify(prev));
            rowUndoStackRef.current.push(snap);
            if (rowUndoStackRef.current.length > MAX_ROW_UNDO) {
                rowUndoStackRef.current.shift();
            }
            return prev.filter((_, i) => i !== idx);
        });
    };

    useEffect(() => {
        if (mainTab !== "planning") {
            return undefined;
        }
        const onKeyDown = (e) => {
            if (!(e.metaKey || e.ctrlKey) || String(e.key).toLowerCase() !== "z" || e.shiftKey) {
                return;
            }
            const el = document.activeElement;
            if (showTitleModal) {
                return;
            }
            if (rowUndoStackRef.current.length === 0) {
                return;
            }
            if (el) {
                if (el.isContentEditable) {
                    return;
                }
                const tag = el.tagName;
                if (tag === "TEXTAREA" || tag === "SELECT") {
                    return;
                }
                /* У полях фільтра/пошуку під «Прогноз» теж відновлюємо рядки (інакше Cmd+Z лишається в полі). */
                const allowRowUndoFromInput =
                    tag === "INPUT" && el.closest?.(".processes-forecast-toolbar") != null;
                if (tag === "INPUT" && !allowRowUndoFromInput) {
                    return;
                }
            }
            e.preventDefault();
            const restored = rowUndoStackRef.current.pop();
            setDraftRows(Array.isArray(restored) ? restored : []);
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [mainTab, showTitleModal]);

    const selectAll = (selected) => {
        setDraftRows((prev) => prev.map((r) => ({ ...r, selected })));
    };

    const forecastTableAllSelected = useMemo(
        () => draftRows.length > 0 && draftRows.every((r) => r.selected !== false),
        [draftRows]
    );
    const forecastTableNoneSelected = useMemo(
        () => draftRows.length > 0 && draftRows.every((r) => r.selected === false),
        [draftRows]
    );
    const forecastTableSelectPartial = useMemo(
        () =>
            draftRows.length > 0 && !forecastTableAllSelected && !forecastTableNoneSelected,
        [draftRows, forecastTableAllSelected, forecastTableNoneSelected]
    );

    const selectedCount = useMemo(
        () => draftRows.filter((r) => !r.excluded && r.selected !== false && qtyFor(r, "cutQty") > 0).length,
        [draftRows]
    );

    useEffect(() => {
        if (materialsPreviewDebounceRef.current) {
            clearTimeout(materialsPreviewDebounceRef.current);
            materialsPreviewDebounceRef.current = null;
        }

        if (mainTab !== "planning") {
            materialsPreviewScheduleIdRef.current += 1;
            setMaterialsPreview({ lines: [], warnings: [], totalOrderUAH: 0 });
            setMaterialsPreviewLoading(false);
            return undefined;
        }
        if (forecastLoading || planLoading) {
            setMaterialsPreviewLoading(false);
            return undefined;
        }
        if (selectedCount === 0) {
            materialsPreviewScheduleIdRef.current += 1;
            setMaterialsPreview({ lines: [], warnings: [], totalOrderUAH: 0 });
            setMaterialsPreviewLoading(false);
            return undefined;
        }

        const scheduleId = (materialsPreviewScheduleIdRef.current += 1);
        setMaterialsPreviewLoading(true);
        materialsPreviewDebounceRef.current = setTimeout(() => {
            materialsPreviewDebounceRef.current = null;
            void (async () => {
                try {
                    const res = await axios.post(
                        `${API}/preview-materials`,
                        { forecastRows: draftRows },
                        { withCredentials: true }
                    );
                    if (materialsPreviewScheduleIdRef.current !== scheduleId) {
                        return;
                    }
                    const lines = Array.isArray(res.data?.materialLines) ? res.data.materialLines : [];
                    const warnings = Array.isArray(res.data?.warnings) ? res.data.warnings : [];
                    const rawTotal = Number(res.data?.totalOrderUAH);
                    const totalOrderUAH = Number.isFinite(rawTotal) ? rawTotal : 0;
                    if (warnings.length > 0) {
                        console.warn("preview-materials warnings", warnings);
                    }
                    setMaterialsPreview({ lines, warnings, totalOrderUAH });
                } catch (e) {
                    if (materialsPreviewScheduleIdRef.current !== scheduleId) {
                        return;
                    }
                    console.error(e);
                    setMaterialsPreview({ lines: [], warnings: [], totalOrderUAH: 0 });
                } finally {
                    if (materialsPreviewScheduleIdRef.current === scheduleId) {
                        setMaterialsPreviewLoading(false);
                    }
                }
            })();
        }, 450);

        return () => {
            materialsPreviewScheduleIdRef.current += 1;
            if (materialsPreviewDebounceRef.current) {
                clearTimeout(materialsPreviewDebounceRef.current);
                materialsPreviewDebounceRef.current = null;
            }
        };
    }, [mainTab, draftRows, forecastLoading, planLoading, selectedCount]);

    const groupedPlanTree = useMemo(() => groupForecastRowsByProductTypeColor(draftRows), [draftRows]);

    const [forecastSort, setForecastSort] = useState({ key: "name", dir: "asc" });
    const [planTreeSort, setPlanTreeSort] = useState({ key: "name", dir: "asc" });

    const [forecastTableFilter, setForecastTableFilter] = useState("");
    const [whPickQuery, setWhPickQuery] = useState("");
    const [whPickSuggestions, setWhPickSuggestions] = useState([]);
    const [whPickOpen, setWhPickOpen] = useState(false);
    const [whPickActive, setWhPickActive] = useState(0);
    const [whPickLoading, setWhPickLoading] = useState(false);
    const whPickDebounceRef = useRef(null);
    const whPickReqIdRef = useRef(0);

    const sortedForecastIndices = useMemo(
        () => sortForecastIndices(draftRows, forecastSort),
        [draftRows, forecastSort]
    );

    const sortedPlanTree = useMemo(
        () => sortGroupedPlanTreeCopy(groupedPlanTree, planTreeSort),
        [groupedPlanTree, planTreeSort]
    );

    const forecastTableTokens = useMemo(
        () => forecastTableFilterTokens(forecastTableFilter),
        [forecastTableFilter]
    );

    const filteredSortedForecastIndices = useMemo(
        () =>
            sortedForecastIndices.filter((idx) =>
                forecastRowMatchesTableFilter(draftRows[idx], forecastTableTokens)
            ),
        [sortedForecastIndices, draftRows, forecastTableTokens]
    );

    useEffect(() => {
        if (mainTab !== "planning") {
            return undefined;
        }
        const q = whPickQuery.trim();
        if (whPickDebounceRef.current) {
            clearTimeout(whPickDebounceRef.current);
            whPickDebounceRef.current = null;
        }
        if (q.length < 1) {
            setWhPickSuggestions([]);
            setWhPickLoading(false);
            return undefined;
        }
        whPickDebounceRef.current = setTimeout(() => {
            whPickDebounceRef.current = null;
            const rid = (whPickReqIdRef.current += 1);
            setWhPickLoading(true);
            void axios
                .get(`${API}/warehouse-variants`, {
                    params: { q, limit: 60 },
                    withCredentials: true,
                })
                .then((res) => {
                    if (whPickReqIdRef.current !== rid) {
                        return;
                    }
                    const variants = Array.isArray(res.data?.variants) ? res.data.variants : [];
                    setWhPickSuggestions(variants);
                    setWhPickActive(0);
                })
                .catch(() => {
                    if (whPickReqIdRef.current !== rid) {
                        return;
                    }
                    setWhPickSuggestions([]);
                })
                .finally(() => {
                    if (whPickReqIdRef.current === rid) {
                        setWhPickLoading(false);
                    }
                });
        }, 280);
        return () => {
            if (whPickDebounceRef.current) {
                clearTimeout(whPickDebounceRef.current);
                whPickDebounceRef.current = null;
            }
        };
    }, [whPickQuery, mainTab]);

    const addVariantFromWarehousePick = useCallback((v) => {
        if (!v?.sku) {
            return;
        }
        let duplicate = false;
        setDraftRows((prev) => {
            if (prev.some((r) => String(r.sku) === String(v.sku))) {
                duplicate = true;
                return prev;
            }
            return [...prev, buildForecastRowFromWarehousePick(v)];
        });
        if (duplicate) {
            setForecastError("Цей SKU вже є в таблиці прогнозу.");
            return;
        }
        setForecastError("");
        setWhPickQuery("");
        setWhPickSuggestions([]);
        setWhPickOpen(false);
    }, []);

    const removeCheckedForecastRows = useCallback(() => {
        setDraftRows((prev) => {
            if (!prev.some((r) => r.selected !== false)) {
                return prev;
            }
            const snap = JSON.parse(JSON.stringify(prev));
            rowUndoStackRef.current.push(snap);
            if (rowUndoStackRef.current.length > MAX_ROW_UNDO) {
                rowUndoStackRef.current.shift();
            }
            return prev.filter((r) => r.selected === false);
        });
    }, []);

    const commitWhPickPlus = useCallback(() => {
        const list = whPickSuggestions;
        if (!list.length) {
            return;
        }
        const i = Math.min(whPickActive, list.length - 1);
        addVariantFromWarehousePick(list[i]);
    }, [whPickSuggestions, whPickActive, addVariantFromWarehousePick]);

    /** Є розрахунок по техкартах, без попереджень, і рекомендоване до закупівлі = 0 (склад покриває потребу). */
    const materialsPreviewStockCovers = useMemo(() => {
        const lines = materialsPreview.lines;
        if (!lines.length || materialsPreview.warnings.length > 0) {
            return false;
        }
        return lines.every((l) => (Number(l.orderQty) || 0) <= 0);
    }, [materialsPreview.lines, materialsPreview.warnings]);

    const toggleForecastSort = useCallback((colKey) => {
        setForecastSort((prev) => {
            if (prev.key === colKey) {
                return { key: colKey, dir: prev.dir === "asc" ? "desc" : "asc" };
            }
            const numericKeys = new Set(["sold", "stock", "speed", "daysLeft", "suggestedQty", "sewQty", "cutQty"]);
            return { key: colKey, dir: numericKeys.has(colKey) ? "desc" : "asc" };
        });
    }, []);

    const togglePlanTreeSortKey = useCallback((key) => {
        setPlanTreeSort((prev) => {
            if (prev.key === key) {
                return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
            }
            return { key, dir: key === "qty" ? "desc" : "asc" };
        });
    }, []);

    const viewingSavedPlan = Boolean(currentPlan?._id);

    const reloadOpenedPlan = useCallback(async () => {
        if (!currentPlan?._id) {
            return;
        }
        const res = await axios.get(`${API}/${currentPlan._id}`, { withCredentials: true });
        const p = res.data;
        setCurrentPlan(p);
        setDraftRows(Array.isArray(p.forecastRows) ? p.forecastRows.map(normalizePlanRowQuantities) : []);
    }, [currentPlan?._id]);

    const postNonSew = useCallback(
        async (mode, row) => {
            const pid = row?.productId;
            if ((mode === "product" || mode === "type") && (!Number.isFinite(Number(pid)) || Number(pid) <= 0)) {
                setForecastError("У рядка немає productId — додайте з прогнозу після синхронізації CRM.");
                return;
            }
            setNonSewBusy(true);
            setForecastError("");
            try {
                await axios.post(
                    `${API}/sewing-exclusions`,
                    {
                        mode,
                        productId: pid,
                        typeLabel: row?.type,
                        sku: row?.sku,
                    },
                    { withCredentials: true }
                );
                if (currentPlan?._id) {
                    await reloadOpenedPlan();
                } else {
                    await loadForecast({ force: true });
                }
            } catch (e) {
                setForecastError(e.response?.data?.error || e.message || "Помилка");
            } finally {
                setNonSewBusy(false);
            }
        },
        [currentPlan?._id, loadForecast, reloadOpenedPlan]
    );

    const removeSewingExclusion = useCallback(
        async (sku) => {
            setNonSewBusy(true);
            setForecastError("");
            try {
                await axios.delete(`${API}/sewing-exclusions/${encodeURIComponent(sku)}`, {
                    withCredentials: true,
                });
                if (currentPlan?._id) {
                    await reloadOpenedPlan();
                } else {
                    await loadForecast({ force: true });
                }
            } catch (e) {
                setForecastError(e.response?.data?.error || e.message || "Помилка");
            } finally {
                setNonSewBusy(false);
            }
        },
        [currentPlan?._id, loadForecast, reloadOpenedPlan]
    );

    const persistForecastToServer = async (planId) => {
        await axios.patch(
            `${API}/${planId}`,
            { forecastRows: draftRows },
            { withCredentials: true }
        );
    };

    const handleSaveChanges = async () => {
        if (!currentPlan?._id) {
            return;
        }
        setSaveLoading(true);
        setForecastError("");
        try {
            await persistForecastToServer(currentPlan._id);
            const res = await axios.get(`${API}/${currentPlan._id}`, { withCredentials: true });
            setCurrentPlan(res.data);
            setDraftRows(
                Array.isArray(res.data?.forecastRows) ? res.data.forecastRows.map(normalizePlanRowQuantities) : []
            );
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка збереження");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCreatePlan = async (title) => {
        setSaveLoading(true);
        setForecastError("");
        try {
            const res = await axios.post(
                API,
                {
                    title: title.trim(),
                    periodKey: period,
                    periodStart: forecastMeta?.periodStart,
                    periodEnd: forecastMeta?.periodEnd,
                    forecastRows: draftRows,
                },
                { withCredentials: true }
            );
            setCurrentPlan(res.data);
            setShowTitleModal(false);
            await refreshPlansList();
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDeletePlan = async (id) => {
        if (!window.confirm("Видалити план?")) {
            return;
        }
        try {
            await axios.delete(`${API}/${id}`, { withCredentials: true });
            if (currentPlan?._id === id) {
                setCurrentPlan(null);
            }
            await refreshPlansList();
            if (mainTab === "supply") {
                void loadSupplyPlans();
            }
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка");
        }
    };

    const closePlanAndReloadForecast = () => {
        setCurrentPlan(null);
        clearRowUndo();
    };

    const supplyAggregateRows = useMemo(() => mergeSupplyAggregateRows(supplyPlans), [supplyPlans]);

    const handleConfirmSupply = async (planId) => {
        setConfirmSupplyBusyId(planId);
        setForecastError("");
        try {
            await axios.post(`${API}/${planId}/confirm-supply`, {}, { withCredentials: true });
            await refreshPlansList();
            if (currentPlan?._id === planId) {
                closePlanAndReloadForecast();
            }
            navigate("/processes/supply");
            await loadSupplyPlans();
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка підтвердження");
        } finally {
            setConfirmSupplyBusyId(null);
        }
    };

    const updateSupplyPlanMaterialLine = (planId, lineIdx, patch) => {
        setSupplyPlans((prev) =>
            prev.map((pl) => {
                if (pl._id !== planId) {
                    return pl;
                }
                const lines = [...(pl.materialLines || [])];
                lines[lineIdx] = { ...lines[lineIdx], ...patch };
                return { ...pl, materialLines: lines };
            })
        );
    };

    const saveSupplyPlanMaterials = async (planId) => {
        const pl = supplyPlans.find((x) => x._id === planId);
        if (!pl) {
            return;
        }
        setSupplySaveLoading(true);
        setForecastError("");
        try {
            await axios.patch(`${API}/${planId}`, { materialLines: pl.materialLines }, { withCredentials: true });
            await loadSupplyPlans();
            await refreshPlansList();
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка збереження матеріалів");
        } finally {
            setSupplySaveLoading(false);
        }
    };

    const moveProductionBatch = async (batchId, fromStage, toStage, qty, action) => {
        setProductionMoveBusyId(String(batchId));
        setForecastError("");
        try {
            await axios.post(
                `${API}/production-batches/${batchId}/move`,
                { fromStage, toStage, qty, action },
                { withCredentials: true }
            );
            if (mainTab === "cut") {
                await loadProductionCardsForStages(["ready_for_cut", "cut_in_progress"]);
            } else if (mainTab === "sew") {
                await loadProductionCardsForStages(["cut_done", "sew_in_progress"]);
            }
        } catch (e) {
            setForecastError(e.response?.data?.error || e.message || "Помилка руху виробництва");
        } finally {
            setProductionMoveBusyId(null);
        }
    };

    return (
        <div className="container-fluid mt-2 mt-md-3 mb-5 pb-5 processes-page">
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-3 mb-3">
                <h3 className="mb-0">Процеси</h3>
                <div className="processes-page-currency w-100 w-md-auto ms-md-auto">
                    <CurrencySwitcher
                        compact
                        currency={processesCurrency}
                        setCurrency={setProcessesCurrency}
                        usdSellRate={usdSellRate}
                        usdBuyRate={usdBuyRate}
                        setUsdSellRate={setUsdSellRate}
                        setUsdBuyRate={setUsdBuyRate}
                    />
                </div>
            </div>

            {forecastError && (
                <Alert
                    variant="warning"
                    dismissible
                    closeLabel="Закрити попередження"
                    onClose={() => setForecastError("")}
                    className="mb-3"
                >
                    {forecastError}
                </Alert>
            )}

            <Nav
                variant="tabs"
                className="mb-3 flex-nowrap"
                activeKey={mainTab}
                onSelect={(k) => k && navigate(`/processes/${k}`)}
            >
                <Nav.Item>
                    <Nav.Link eventKey="planning" title="Планування SEO: прогноз, збережений план, матеріали за обраними позиціями">
                        Планування (SEO)
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="supply" title="Підтверджені плани: матеріали на крій, поле «Замовити», зведення для закупівель">
                        Забезпечення матеріалами
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="cut" title="Розкрій — розділ у розробці">
                        Розкрій
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="sew" title="Пошив — розділ у розробці">
                        Пошив
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="dashboard" title="Дашборд виробництва по SKU та етапах">
                        Дашборд
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {mainTab === "planning" && (
                <div>
                    <div className="row g-3">
                        <div className="col-lg-3">
                            <div className="border rounded p-3 h-100">
                                <h6 className="mb-2">Збережені плани</h6>
                                {listLoading ? (
                                    <Spinner size="sm" />
                                ) : plansList.length === 0 ? (
                                    <div className="text-muted small">Поки порожньо</div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {plansList.map((p) => {
                                            const isActive = currentPlan?._id === p._id && !p.supplyConfirmed;
                                            return (
                                                <Card
                                                    key={p._id}
                                                    className={`border border-2${p.supplyConfirmed ? " bg-light" : ""} ${
                                                        isActive
                                                            ? "border-primary shadow"
                                                            : "border-secondary shadow-sm"
                                                    }`}
                                                >
                                                    <Card.Body className="position-relative p-3">
                                                        <Button
                                                            type="button"
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="position-absolute top-0 end-0 mt-2 me-2 rounded-circle p-0 d-flex align-items-center justify-content-center"
                                                            style={{ width: 28, height: 28, lineHeight: 1 }}
                                                            title="Видалити план"
                                                            aria-label="Видалити план"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                void handleDeletePlan(p._id);
                                                            }}
                                                        >
                                                            ×
                                                        </Button>
                                                        <button
                                                            type="button"
                                                            className={`btn btn-link text-start text-decoration-none p-0 w-100 border-0 pe-5 ${
                                                                p.supplyConfirmed ? "text-secondary" : "text-body"
                                                            }`}
                                                            title="Відкрити план для перегляду та редагування рядків"
                                                            onClick={() => void openPlan(p._id)}
                                                        >
                                                            <div className="fw-semibold small pe-1">
                                                                {p.title || "Без назви"}
                                                                {p.supplyConfirmed ? (
                                                                    <span className="ms-1 badge bg-success">
                                                                        забезпечення
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {p.periodStart} — {p.periodEnd} ·{" "}
                                                                {p.forecastCount ?? 0} SKU
                                                                {p.materialCount != null && p.materialCount > 0
                                                                    ? ` · ${p.materialCount} мат.`
                                                                    : ""}
                                                            </div>
                                                        </button>
                                                        <div className="d-flex justify-content-end mt-2 pt-2 border-top border-light">
                                                            <Button
                                                                variant={
                                                                    p.supplyConfirmed ? "success" : "outline-primary"
                                                                }
                                                                size="sm"
                                                                disabled={
                                                                    Boolean(p.supplyConfirmed) ||
                                                                    confirmSupplyBusyId === p._id
                                                                }
                                                                title={
                                                                    p.supplyConfirmed
                                                                        ? "План у забезпеченні"
                                                                        : "Підтвердити для відділу матеріалів"
                                                                }
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    void handleConfirmSupply(p._id);
                                                                }}
                                                            >
                                                                {confirmSupplyBusyId === p._id
                                                                    ? "…"
                                                                    : p.supplyConfirmed
                                                                      ? "Підтверджено"
                                                                      : "Підтвердити"}
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-9">
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 processes-planning-toolbar">
                                {!viewingSavedPlan && (
                                    <>
                                        <Form.Select
                                            className="w-auto"
                                            style={{ maxWidth: 160 }}
                                            value={period}
                                            onChange={(e) => setPeriod(e.target.value)}
                                            aria-label="Період продажів"
                                        >
                                            <option value="last7">7 днів</option>
                                            <option value="last14">14 днів</option>
                                        </Form.Select>
                                        <Button
                                            variant="primary"
                                            className="text-nowrap"
                                            disabled={saveLoading || draftRows.length === 0}
                                            title="Створити новий план на основі поточного прогнозу (відкриється вікно назви)"
                                            onClick={() => {
                                                const d = new Date();
                                                setModalTitle(
                                                    `План ${d.toLocaleDateString("uk-UA")}, ${d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })} (${period})`
                                                );
                                                setShowTitleModal(true);
                                            }}
                                        >
                                            Новий план…
                                        </Button>
                                    </>
                                )}
                                {viewingSavedPlan && (
                                    <>
                                        <div className="me-auto">
                                            <div className="fw-semibold">
                                                {currentPlan.title || "План"}
                                            </div>
                                            <div className="text-muted small">
                                                {forecastMeta?.periodStart} — {forecastMeta?.periodEnd}
                                                {currentPlan.status === "materials_ready" ? (
                                                    <span className="ms-1">· матеріали зведені</span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-secondary"
                                            className="text-nowrap"
                                            title="Закрити план і повернутися до таблиці прогнозу без збереження через цю кнопку (зміни не скасовуються автоматично)"
                                            onClick={() => closePlanAndReloadForecast()}
                                        >
                                            До прогнозу
                                        </Button>
                                    </>
                                )}
                            </div>

                            {forecastMeta && !viewingSavedPlan && (
                                <p className="text-muted small">
                                    Період: {forecastMeta.periodStart} — {forecastMeta.periodEnd}
                                    {forecastMeta.days != null ? ` (${forecastMeta.days} дн.)` : ""};{" "}
                                    <strong>без сьогодні</strong> — у швидкості лише завершені дні до вчора включно. У
                                    списку лише
                                    варіанти, що за поточною швидкістю можуть{" "}
                                    <strong>закінчитися приблизно за {forecastMeta.runwayTargetDays ?? 7} дн.</strong>{" "}
                                    (або вже в мінусі). Рядки з «трохи продали — залишок великий» не показуємо.
                                </p>
                            )}
                            {viewingSavedPlan && (
                                <p className="text-muted small">
                                    Редагуйте кількість за розміром окремо для пошиву і крою; натисніть «Зберегти зміни»
                                    внизу списку. Матеріали рахуються по колонці «Крій». «Виключити з пошиву…» —
                                    додати варіант у допродаж (повний список — на «Складах»).
                                </p>
                            )}

                            <h5 className="mb-2 mt-1">Прогноз</h5>

                            {mainTab === "planning" && (
                                <div className="mb-3 processes-forecast-toolbar">
                                    <div className="d-flex flex-wrap gap-2 processes-forecast-toolbar-row mb-2">
                                        <Form.Control
                                            className="flex-grow-1"
                                            style={{ flex: "1 1 220px", minWidth: 200 }}
                                            placeholder="Фільтр таблиці: кілька слів (назва, колір, розмір, SKU) — усі мають збігатися"
                                            value={forecastTableFilter}
                                            onChange={(e) => setForecastTableFilter(e.target.value)}
                                            aria-label="Фільтр рядків прогнозу"
                                        />
                                        <div
                                            className="position-relative processes-forecast-wh-wrap flex-grow-1"
                                            style={{ flex: "1 1 220px", minWidth: 200 }}
                                        >
                                            <Form.Control
                                                className="w-100"
                                                placeholder="Додати зі складу варіант товару (клік по рядку або Enter)"
                                                value={whPickQuery}
                                                onChange={(e) => {
                                                    setWhPickQuery(e.target.value);
                                                    setWhPickOpen(true);
                                                }}
                                                onFocus={() => setWhPickOpen(true)}
                                                onBlur={() => {
                                                    setTimeout(() => setWhPickOpen(false), 180);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "ArrowDown") {
                                                        if (whPickSuggestions.length === 0) {
                                                            return;
                                                        }
                                                        e.preventDefault();
                                                        setWhPickActive((a) =>
                                                            Math.min(a + 1, Math.max(0, whPickSuggestions.length - 1))
                                                        );
                                                        setWhPickOpen(true);
                                                    } else if (e.key === "ArrowUp") {
                                                        if (whPickSuggestions.length === 0) {
                                                            return;
                                                        }
                                                        e.preventDefault();
                                                        setWhPickActive((a) => Math.max(0, a - 1));
                                                    } else if (e.key === "Enter") {
                                                        if (whPickSuggestions.length > 0) {
                                                            e.preventDefault();
                                                            commitWhPickPlus();
                                                        } else if (whPickQuery.trim()) {
                                                            e.preventDefault();
                                                            void (async () => {
                                                                try {
                                                                    const res = await axios.get(
                                                                        `${API}/warehouse-variants`,
                                                                        {
                                                                            params: {
                                                                                q: whPickQuery.trim(),
                                                                                limit: 5,
                                                                            },
                                                                            withCredentials: true,
                                                                        }
                                                                    );
                                                                    const arr = Array.isArray(res.data?.variants)
                                                                        ? res.data.variants
                                                                        : [];
                                                                    if (arr.length === 1) {
                                                                        addVariantFromWarehousePick(arr[0]);
                                                                    } else if (arr.length === 0) {
                                                                        setForecastError(
                                                                            "Нічого не знайдено на складі."
                                                                        );
                                                                    } else {
                                                                        setWhPickSuggestions(arr);
                                                                        setWhPickOpen(true);
                                                                        setWhPickActive(0);
                                                                    }
                                                                } catch (err) {
                                                                    setForecastError(
                                                                        err.response?.data?.error ||
                                                                            err.message ||
                                                                            "Помилка пошуку"
                                                                    );
                                                                }
                                                            })();
                                                        }
                                                    } else if (e.key === "Escape") {
                                                        setWhPickOpen(false);
                                                    }
                                                }}
                                                aria-label="Додати зі складу варіант товару в прогноз"
                                                aria-autocomplete="list"
                                                aria-expanded={whPickOpen && whPickSuggestions.length > 0}
                                            />
                                            {whPickOpen && (whPickSuggestions.length > 0 || whPickLoading) ? (
                                                <ListGroup
                                                    className="position-absolute w-100 shadow-sm border rounded mt-1"
                                                    style={{
                                                        zIndex: 1080,
                                                        maxHeight: 280,
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    {whPickLoading && whPickSuggestions.length === 0 ? (
                                                        <ListGroup.Item className="small text-muted py-2">
                                                            Пошук…
                                                        </ListGroup.Item>
                                                    ) : null}
                                                    {whPickSuggestions.map((v, i) => (
                                                        <ListGroup.Item
                                                            key={v.sku}
                                                            action
                                                            active={i === whPickActive}
                                                            className="small py-2"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onMouseEnter={() => setWhPickActive(i)}
                                                            onClick={() => addVariantFromWarehousePick(v)}
                                                        >
                                                            {v.label}
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            ) : null}
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            className="flex-shrink-0 align-self-stretch d-inline-flex align-items-center justify-content-center px-2"
                                            disabled={!draftRows.some((r) => r.selected !== false)}
                                            title="Видалити обрані рядки (з галочкою «Обрати»). Зніміть галочку в рядках, які мають залишитися."
                                            aria-label="Видалити обрані рядки з прогнозу"
                                            onClick={() => removeCheckedForecastRows()}
                                        >
                                            <ListX size={20} strokeWidth={2} aria-hidden />
                                        </Button>
                                    </div>
                                    <p className="text-muted small mb-0">
                                        Галочка «Обрати» — участь у зведенні матеріалів. Червона кнопка з іконкою
                                        «список + хрестики» праворуч масово прибирає з таблиці рядки з увімкненою
                                        галочкою — зніміть галочку в рядках, які мають залишитися.
                                    </p>
                                </div>
                            )}

                            {forecastLoading || planLoading ? (
                                <Spinner animation="border" />
                            ) : viewingSavedPlan ? (
                                <div className="border rounded px-3 py-2 processes-plan-tree">
                                    {draftRows.length === 0 ? (
                                        <p className="text-muted mb-0 small">Немає позицій у плані.</p>
                                    ) : null}
                                    {draftRows.length > 0 ? (
                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2 pb-2 border-bottom">
                                            <span className="text-muted small">Сортування:</span>
                                            <Button
                                                variant={planTreeSort.key === "name" ? "primary" : "outline-secondary"}
                                                size="sm"
                                                title="Сортувати дерево плану за назвою товару; повторний клік — змінити напрям (А→Я / Я→А)"
                                                onClick={() => togglePlanTreeSortKey("name")}
                                            >
                                                За назвою
                                                {planTreeSort.key === "name"
                                                    ? planTreeSort.dir === "asc"
                                                        ? " (А→Я)"
                                                        : " (Я→А)"
                                                    : ""}
                                            </Button>
                                            <Button
                                                variant={planTreeSort.key === "qty" ? "primary" : "outline-secondary"}
                                                size="sm"
                                                title="Сортувати за сумарною кількістю в плані по товару; повторний клік — більше або менше спочатку"
                                                onClick={() => togglePlanTreeSortKey("qty")}
                                            >
                                                За кількістю (план)
                                                {planTreeSort.key === "qty"
                                                    ? planTreeSort.dir === "desc"
                                                        ? " (більше спочатку)"
                                                        : " (менше спочатку)"
                                                    : ""}
                                            </Button>
                                        </div>
                                    ) : null}
                                    {sortedPlanTree.map((product) => {
                                        const showTypeHeaders =
                                            product.types.length > 1 ||
                                            (product.types.length === 1 && product.types[0].type !== "—");
                                        const productSewTotal = sumQtyForProduct(product, "sewQty");
                                        const productCutTotal = sumQtyForProduct(product, "cutQty");
                                        return (
                                        <div key={product.productKey} className="mb-2 pb-2 border-bottom">
                                            <div className="fw-semibold fs-6 mb-1 lh-sm">
                                                {product.name}{" "}
                                                <span className="text-muted fw-normal">
                                                    (пошив {fmtNum(productSewTotal)} · крій {fmtNum(productCutTotal)})
                                                </span>
                                            </div>
                                            {product.types.map(({ type, colors }) => (
                                                <div
                                                    key={`${product.productKey}-t-${type}`}
                                                    className={showTypeHeaders ? "ps-4 mb-1" : "mb-1"}
                                                >
                                                    {showTypeHeaders ? (
                                                        <div className="text-muted small mb-1 lh-sm">
                                                            Тип: <span className="text-body">{type}</span>
                                                        </div>
                                                    ) : null}
                                                    {colors.map(({ color, items }) => (
                                                        <div
                                                            key={`${product.productKey}-${type}-${color}`}
                                                            className="ps-4 mb-1"
                                                        >
                                                            <div className="text-secondary small fw-medium mb-1 lh-sm">
                                                                {color}
                                                            </div>
                                                            <ul className="list-unstyled ps-4 mb-0">
                                                                {items.map(({ idx, row }) => {
                                                                    if (!forecastRowMatchesTableFilter(row, forecastTableTokens)) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                            <li
                                                                key={`${row.sku}-${idx}`}
                                                                className={`d-flex flex-wrap align-items-center gap-2 gap-y-1 py-1 border-bottom border-light${
                                                                    Number(row.stock) < 0 ? " text-danger" : ""
                                                                }`}
                                                            >
                                                                <span className="text-nowrap">
                                                                    Розмір{" "}
                                                                    <strong>{row.size || "—"}</strong>
                                                                </span>
                                                                <span
                                                                    className={`small${
                                                                        Number(row.stock) < 0 ? "" : " text-muted"
                                                                    }`}
                                                                >
                                                                    SKU {row.sku}
                                                                </span>
                                                                <span
                                                                    className={`small${
                                                                        Number(row.stock) < 0 ? "" : " text-muted"
                                                                    }`}
                                                                >
                                                                    прод. {fmtNum(row.sold)} · зал.{" "}
                                                                    {fmtNum(row.stock)}
                                                                </span>
                                                                <div className="d-flex align-items-center gap-2 ms-auto">
                                                                    <span className="small text-nowrap">
                                                                        Пошив
                                                                    </span>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min={0}
                                                                        step={1}
                                                                        size="sm"
                                                                        style={{ width: 88 }}
                                                                        className="text-end"
                                                                        value={qtyFor(row, "sewQty")}
                                                                        onChange={(e) =>
                                                                            updateRow(idx, {
                                                                                sewQty: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                                                                planQty: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                                                            })
                                                                        }
                                                                        aria-label={`Кількість на пошив, ${row.sku}`}
                                                                    />
                                                                    <span className="small text-nowrap">
                                                                        Крій
                                                                    </span>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min={0}
                                                                        step={1}
                                                                        size="sm"
                                                                        style={{ width: 88 }}
                                                                        className="text-end"
                                                                        value={qtyFor(row, "cutQty")}
                                                                        onChange={(e) =>
                                                                            updateRow(idx, {
                                                                                cutQty: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                                                            })
                                                                        }
                                                                        aria-label={`Кількість на крій, ${row.sku}`}
                                                                    />
                                                                </div>
                                                                <div className="d-inline-flex align-items-center">
                                                                    <AppCheckbox
                                                                        id={`proc-tree-sel-${idx}-${String(row.sku || "").replace(/\W/g, "") || "row"}`}
                                                                        label=""
                                                                        title={`Врахувати цей рядок у зведенні матеріалів (SKU ${row.sku || "—"})`}
                                                                        aria-label={`Обрати для закупівлі, ${row.sku}`}
                                                                        checked={row.selected !== false}
                                                                        onChange={(e) =>
                                                                            updateRow(idx, {
                                                                                selected: e.target.checked,
                                                                            })
                                                                        }
                                                                        className="mb-0"
                                                                    />
                                                                </div>
                                                                <div className="d-inline-flex flex-nowrap align-items-center gap-1">
                                                                    <Dropdown align="end" className="d-inline-block">
                                                                        <Dropdown.Toggle
                                                                            variant="link"
                                                                            className="text-secondary p-0 small py-0 text-decoration-none"
                                                                            disabled={nonSewBusy}
                                                                            title="Виключити з пошиву: увесь товар, усі варіанти типу або лише цей SKU"
                                                                        >
                                                                            Виключити з пошиву…
                                                                        </Dropdown.Toggle>
                                                                        <Dropdown.Menu>
                                                                            <Dropdown.Item
                                                                                title="Виключити з пошиву весь товар — усі типи, кольори та розміри"
                                                                                onClick={() =>
                                                                                    void postNonSew("product", row)
                                                                                }
                                                                            >
                                                                                Увесь товар (усі варіанти)
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Item
                                                                                title="Виключити з пошиву всі варіанти цього типу (усі кольори та розміри)"
                                                                                onClick={() => void postNonSew("type", row)}
                                                                            >
                                                                                Усі варіанти цього типу
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Item
                                                                                title="Виключити з пошиву лише цей варіант (один SKU)"
                                                                                onClick={() => void postNonSew("sku", row)}
                                                                            >
                                                                                Лише цей SKU
                                                                            </Dropdown.Item>
                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                    <Button
                                                                        type="button"
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                                                        title="Вилучити рядок з плану (після видалення: Ctrl+Z / Cmd+Z, якщо фокус не в полі числа)"
                                                                        aria-label="Вилучити рядок з плану"
                                                                        onClick={() => removeRow(idx)}
                                                                    >
                                                                        <Trash2 size={18} aria-hidden />
                                                                    </Button>
                                                                </div>
                                                            </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        );
                                    })}
                                    <div className="pt-2 mt-2 d-flex flex-wrap align-items-center gap-2 border-top">
                                        <Button
                                            variant="success"
                                            size="sm"
                                            disabled={!currentPlan?._id || saveLoading}
                                            title="Зберегти зміни відкритого плану на сервері"
                                            onClick={() => void handleSaveChanges()}
                                        >
                                            Зберегти зміни
                                        </Button>
                                        <span className="text-muted small">
                                            Ctrl+Z / Cmd+Z після «Вилучити», якщо фокус не в полі числа
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive border rounded">
                                    <Table hover size="sm" className="mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="align-middle" style={{ minWidth: 108 }}>
                                                    <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                        <span className="small text-nowrap">Обрати</span>
                                                        <AppCheckbox
                                                            id="proc-forecast-select-all-master"
                                                            label=""
                                                            className="mb-0 flex-shrink-0"
                                                            indeterminate={forecastTableSelectPartial}
                                                            checked={forecastTableAllSelected}
                                                            disabled={draftRows.length === 0}
                                                            style={{
                                                                cursor: draftRows.length ? "pointer" : "not-allowed",
                                                            }}
                                                            onChange={() => {
                                                                if (forecastTableAllSelected) {
                                                                    selectAll(false);
                                                                } else {
                                                                    selectAll(true);
                                                                }
                                                            }}
                                                            title={
                                                                forecastTableSelectPartial
                                                                    ? "Обрано частину рядків; клік — обрати всі"
                                                                    : forecastTableAllSelected
                                                                      ? "Зняти всі галочки"
                                                                      : "Обрати всі рядки"
                                                            }
                                                            aria-label="Обрати або зняти всі рядки прогнозу"
                                                        />
                                                    </div>
                                                </th>
                                                <ForecastSortableTh
                                                    colKey="sku"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="small"
                                                >
                                                    SKU
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="name"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                >
                                                    Назва
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="color"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                >
                                                    Колір
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="size"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                >
                                                    Розмір
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="sold"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                >
                                                    Продано
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="stock"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                >
                                                    Залишок
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="speed"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                >
                                                    Швидк.
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="daysLeft"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                >
                                                    Днів залиш.
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="suggestedQty"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                >
                                                    Підказка
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="sewQty"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                    style={{ minWidth: 88 }}
                                                >
                                                    Пошив
                                                </ForecastSortableTh>
                                                <ForecastSortableTh
                                                    colKey="cutQty"
                                                    sortState={forecastSort}
                                                    onSort={toggleForecastSort}
                                                    className="text-end"
                                                    style={{ minWidth: 88 }}
                                                >
                                                    Крій
                                                </ForecastSortableTh>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredSortedForecastIndices.length === 0 &&
                                            draftRows.length > 0 &&
                                            forecastTableTokens.length > 0 ? (
                                                <tr>
                                                    <td colSpan={13} className="text-muted small text-center py-3">
                                                        Немає рядків, що відповідають фільтру.
                                                    </td>
                                                </tr>
                                            ) : null}
                                            {filteredSortedForecastIndices.map((idx) => {
                                                const row = draftRows[idx];
                                                return (
                                                <tr
                                                    key={`${row.sku}-${idx}`}
                                                    className={Number(row.stock) < 0 ? "text-danger" : undefined}
                                                >
                                                    <td className="text-center align-middle">
                                                        <div className="d-inline-flex justify-content-center">
                                                            <AppCheckbox
                                                                id={`proc-plan-sel-${idx}-${String(row.sku || "").replace(/\W/g, "") || "row"}`}
                                                                label=""
                                                                title={`Врахувати цей рядок у зведенні матеріалів (SKU ${row.sku || "—"})`}
                                                                aria-label={`Обрати для закупівлі, ${row.sku || "рядок"}`}
                                                                checked={row.selected !== false}
                                                                onChange={(e) =>
                                                                    updateRow(idx, { selected: e.target.checked })
                                                                }
                                                                className="mb-0"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td
                                                        className={`small${
                                                            Number(row.stock) < 0 ? "" : " text-muted"
                                                        }`}
                                                    >
                                                        {row.sku}
                                                    </td>
                                                    <td>
                                                        {row.name}{" "}
                                                        <span
                                                            className={
                                                                Number(row.stock) < 0 ? "small" : "text-muted small"
                                                            }
                                                        >
                                                            (пошив {fmtNum(qtyFor(row, "sewQty"))} · крій{" "}
                                                            {fmtNum(qtyFor(row, "cutQty"))})
                                                        </span>
                                                    </td>
                                                    <td>{row.color}</td>
                                                    <td>{row.size}</td>
                                                    <td className="text-end">{fmtNum(row.sold)}</td>
                                                    <td
                                                        className={`text-end${
                                                            Number(row.stock) < 0 ? " fw-semibold" : ""
                                                        }`}
                                                    >
                                                        {fmtNum(row.stock)}
                                                    </td>
                                                    <td className="text-end">{fmtNum(row.speed)}</td>
                                                    <td className="text-end">{daysLeftLabel(row.daysLeft)}</td>
                                                    <td className="text-end">{fmtNum(row.suggestedQty)}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={1}
                                                            size="sm"
                                                            className="text-end"
                                                            value={qtyFor(row, "sewQty")}
                                                            onChange={(e) =>
                                                                updateRow(idx, {
                                                                    sewQty: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                                                    planQty: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                                                })
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={1}
                                                            size="sm"
                                                            className="text-end"
                                                            value={qtyFor(row, "cutQty")}
                                                            onChange={(e) =>
                                                                updateRow(idx, {
                                                                    cutQty: Math.max(
                                                                        0,
                                                                        Math.round(Number(e.target.value) || 0)
                                                                    ),
                                                                })
                                                            }
                                                        />
                                                    </td>
                                                    <td className="text-nowrap align-middle">
                                                        <div className="d-inline-flex flex-nowrap align-items-center gap-1">
                                                            <Dropdown align="end" className="d-inline-block">
                                                                <Dropdown.Toggle
                                                                    variant="link"
                                                                    className="text-secondary p-0 small text-decoration-none"
                                                                    disabled={nonSewBusy}
                                                                    title="Виключити з пошиву: увесь товар, усі варіанти типу або лише цей SKU"
                                                                >
                                                                    Виключити з пошиву…
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    <Dropdown.Item
                                                                        title="Виключити з пошиву весь товар — усі типи, кольори та розміри"
                                                                        onClick={() => void postNonSew("product", row)}
                                                                    >
                                                                        Увесь товар (усі варіанти)
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        title="Виключити з пошиву всі варіанти цього типу (усі кольори та розміри)"
                                                                        onClick={() => void postNonSew("type", row)}
                                                                    >
                                                                        Усі варіанти цього типу
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        title="Виключити з пошиву лише цей варіант (один SKU)"
                                                                        onClick={() => void postNonSew("sku", row)}
                                                                    >
                                                                        Лише цей SKU
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                            <Button
                                                                type="button"
                                                                variant="link"
                                                                size="sm"
                                                                className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                                                title="Вилучити рядок з прогнозу / плану (після видалення: Ctrl+Z / Cmd+Z, якщо фокус не в полі числа)"
                                                                aria-label="Вилучити рядок з прогнозу"
                                                                onClick={() => removeRow(idx)}
                                                            >
                                                                <Trash2 size={18} aria-hidden />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {!forecastLoading && !planLoading && (
                                <div className="mt-4">
                                    <>
                                        <h5 className="mb-2">Матеріали за обраними в прогнозі позиціями</h5>
                                        {selectedCount === 0 ? (
                                            <p className="text-muted small mb-0">
                                                Позначте галочками рядки з кроєм &gt; 0 — тут з’явиться зведення
                                                матеріалів за техкартами (без збереження плану).
                                            </p>
                                        ) : materialsPreviewLoading ? (
                                            <div className="d-flex align-items-center gap-2 text-muted small">
                                                <Spinner animation="border" size="sm" />
                                                Розрахунок матеріалів…
                                            </div>
                                        ) : materialsPreview.lines.length === 0 ? (
                                            materialsPreview.warnings.length > 0 ? (
                                                <p className="text-warning small mb-0">
                                                    Не вдалося зібрати матеріали по частині обраних позицій: перевірте
                                                    техкарти (товар + колір у CRM), наявність productId і кольору
                                                    варіанта. Деталі — у консолі браузера (F12 → Console).
                                                </p>
                                            ) : (
                                                <p className="text-muted small mb-0">
                                                    Таблиця порожня: зазвичай рядки не потрапили в розрахунок (немає
                                                    productId або кольору, варіант у допродажі (виключений з пошиву), або
                                                    дані ще не
                                                    підтягнулися з CRM). Уточніть картку товару; для збереження розкладу в
                                                    плані створіть план і підтвердіть його для забезпечення матеріалами.
                                                </p>
                                            )
                                        ) : (
                                            <>
                                                {materialsPreviewStockCovers ? (
                                                    <Alert variant="success" className="py-2 small mb-2">
                                                        За обраними позиціями на складі вистачає матеріалів — додатково
                                                        нічого закуповувати не потрібно (колонка «Рекоменд. замовити» —
                                                        0).
                                                    </Alert>
                                                ) : null}
                                                {!materialsPreviewStockCovers &&
                                                materialsPreview.warnings.length > 0 ? (
                                                    <p className="text-warning small mb-2">
                                                        Увага: {materialsPreview.warnings.length} поз. без техкарти або з
                                                        помилкою (деталі в консолі браузера).
                                                    </p>
                                                ) : null}
                                                <p className="text-muted small mb-2">
                                                    Ціна за одиницю — з картки матеріалу (як на складі). Колонки ціни
                                                    та суми — у валюті з перемикача зверху справа (₴ або $ за курсом
                                                    продажу USD).
                                                </p>
                                                <div className="table-responsive border rounded">
                                                    <Table size="sm" className="mb-0 align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Матеріал</th>
                                                                <th>Колір</th>
                                                                <th>Од.</th>
                                                                <th className="text-end">На складі</th>
                                                                <th className="text-end">Потрібно</th>
                                                                <th className="text-end">Рекоменд. замовити</th>
                                                                <th className="text-end">
                                                                    Ціна за од.,{" "}
                                                                    {processesCurrency === "USD" ? "$" : "₴"}
                                                                </th>
                                                                <th className="text-end">
                                                                    Сума, {processesCurrency === "USD" ? "$" : "₴"}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {materialsPreview.lines.map((line, idx) => (
                                                                <tr key={String(line.materialId) || idx}>
                                                                    <td>{line.typeName}</td>
                                                                    <td>{line.color}</td>
                                                                    <td>{line.unit}</td>
                                                                    <td className="text-end">{fmtNum(line.stockQty)}</td>
                                                                    <td className="text-end">{fmtNum(line.requiredQty)}</td>
                                                                    <td className="text-end">{fmtNum(line.orderQty)}</td>
                                                                    <td className="text-end">
                                                                        {Number(line.unitPriceUAH) > 0
                                                                            ? fmtMaterialMoneyFromUAH(
                                                                                  line.unitPriceUAH,
                                                                                  processesCurrency,
                                                                                  usdSellRate
                                                                              )
                                                                            : "—"}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {fmtMaterialMoneyFromUAH(
                                                                            line.orderAmountUAH ?? 0,
                                                                            processesCurrency,
                                                                            usdSellRate
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-light">
                                                            <tr className="fw-semibold">
                                                                <td colSpan={6} className="text-end">
                                                                    Ймовірна сума закупівлі (по «Рекоменд. замовити»)
                                                                </td>
                                                                <td />
                                                                <td className="text-end">
                                                                    {fmtMaterialMoneyFromUAH(
                                                                        materialsPreview.totalOrderUAH,
                                                                        processesCurrency,
                                                                        usdSellRate
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                </div>
                                            </>
                                        )}
                                    </>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {mainTab === "supply" && (
                <div>
                    <p className="text-muted small mb-3">
                        Підтверджені плани з «Планування»: скільки матеріалу потрібно на крій, залишок на складі та
                        поле «Замовити» (за замовчуванням — нестача до потреби). Нижче — зведений список для менеджера
                        закупівель.
                    </p>
                    {supplyLoading ? (
                        <Spinner animation="border" />
                    ) : supplyPlans.length === 0 ? (
                        <Alert variant="secondary" className="mb-0">
                            Немає підтверджених планів. У списку зліва в «Плануванні» натисніть «Підтвердити» біля
                            потрібного плану (за наявності позицій з кроєм &gt; 0; матеріали зазвичай збираються
                            автоматично при підтвердженні плану).
                        </Alert>
                    ) : (
                        <>
                            {supplyPlans.map((plan) => (
                                <Card key={plan._id} className="mb-3">
                                    <Card.Header className="py-2 d-flex flex-wrap justify-content-between gap-2 align-items-center">
                                        <div>
                                            <strong>{plan.title}</strong>
                                            <span className="text-muted small ms-2">
                                                {plan.periodStart} — {plan.periodEnd}
                                            </span>
                                        </div>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            disabled={supplySaveLoading}
                                            title="Записати на сервер поля «Замовити» та примітки по рядках матеріалів цього плану"
                                            onClick={() => void saveSupplyPlanMaterials(plan._id)}
                                        >
                                            Зберегти матеріали плану
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="pt-2 pb-3">
                                        {!plan.materialLines?.length ? (
                                            <p className="text-muted small mb-0">Немає рядків матеріалів.</p>
                                        ) : (
                                            <div className="table-responsive border rounded">
                                                <Table size="sm" className="mb-0 align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Матеріал</th>
                                                            <th>Колір</th>
                                                            <th>Од.</th>
                                                            <th className="text-end">Потрібно на крій</th>
                                                            <th className="text-end">На складі</th>
                                                            <th className="text-end" style={{ minWidth: 100 }}>
                                                                Замовити
                                                            </th>
                                                            <th className="text-end">
                                                                Ціна за од.,{" "}
                                                                {processesCurrency === "USD" ? "$" : "₴"}
                                                            </th>
                                                            <th className="text-end">
                                                                Сума, {processesCurrency === "USD" ? "$" : "₴"}
                                                            </th>
                                                            <th>Примітка</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {plan.materialLines.map((line, idx) => {
                                                            const unitUah = effectiveMaterialUnitUAH(
                                                                line,
                                                                usdSellRate
                                                            );
                                                            const rowSum =
                                                                Math.round(
                                                                    unitUah * (Number(line.orderQty) || 0) * 100
                                                                ) / 100;
                                                            return (
                                                            <tr key={String(line.materialId) || idx}>
                                                                <td>{line.typeName}</td>
                                                                <td>{line.color}</td>
                                                                <td>{line.unit}</td>
                                                                <td className="text-end">
                                                                    {fmtNum(line.requiredQty)}
                                                                </td>
                                                                <td className="text-end">{fmtNum(line.stockQty)}</td>
                                                                <td>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min={0}
                                                                        step={0.01}
                                                                        size="sm"
                                                                        className="text-end"
                                                                        value={line.orderQty ?? 0}
                                                                        onChange={(e) =>
                                                                            updateSupplyPlanMaterialLine(
                                                                                plan._id,
                                                                                idx,
                                                                                {
                                                                                    orderQty: Math.max(
                                                                                        0,
                                                                                        Number(e.target.value) || 0
                                                                                    ),
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="text-end">
                                                                    {unitUah > 0
                                                                        ? fmtMaterialMoneyFromUAH(
                                                                              unitUah,
                                                                              processesCurrency,
                                                                              usdSellRate
                                                                          )
                                                                        : "—"}
                                                                </td>
                                                                <td className="text-end">
                                                                    {unitUah > 0 || (Number(line.orderQty) || 0) > 0
                                                                        ? fmtMaterialMoneyFromUAH(
                                                                              rowSum,
                                                                              processesCurrency,
                                                                              usdSellRate
                                                                          )
                                                                        : "—"}
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        value={line.note || ""}
                                                                        onChange={(e) =>
                                                                            updateSupplyPlanMaterialLine(
                                                                                plan._id,
                                                                                idx,
                                                                                {
                                                                                    note: e.target.value,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot className="table-light">
                                                        <tr className="fw-semibold">
                                                            <td colSpan={6} className="text-end">
                                                                Ймовірна сума закупівлі (по «Замовити»)
                                                            </td>
                                                            <td />
                                                            <td className="text-end">
                                                                {fmtMaterialMoneyFromUAH(
                                                                    Math.round(
                                                                        (plan.materialLines || []).reduce(
                                                                            (s, ln) =>
                                                                                s +
                                                                                effectiveMaterialUnitUAH(
                                                                                    ln,
                                                                                    usdSellRate
                                                                                ) *
                                                                                    (Number(ln.orderQty) || 0),
                                                                            0
                                                                        ) * 100
                                                                    ) / 100,
                                                                    processesCurrency,
                                                                    usdSellRate
                                                                )}
                                                            </td>
                                                            <td />
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            ))}

                            <h5 className="mt-4 mb-2">Список матеріалів на закупівлю (зведення)</h5>
                            <p className="text-muted small mb-2">
                                Підсумок по всіх планах вище: сума «Замовити» збережених у планах рядків, рекомендовано
                                max(0, потрібно − склад). Щоб змінити закупівлю — редагуйте «Замовити» у картці плану та
                                натисніть «Зберегти матеріали плану».
                            </p>
                            <div className="table-responsive border rounded">
                                <Table striped size="sm" className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Матеріал</th>
                                            <th>Колір</th>
                                            <th>Од.</th>
                                            <th className="text-end">Потрібно (усього)</th>
                                            <th className="text-end">На складі</th>
                                            <th className="text-end">Рекомендовано</th>
                                            <th className="text-end">Замовити (сума з планів)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {supplyAggregateRows.map((r) => (
                                            <tr key={String(r.materialId)}>
                                                <td>{r.typeName}</td>
                                                <td>{r.color}</td>
                                                <td>{r.unit}</td>
                                                <td className="text-end">{fmtNum(r.requiredQty)}</td>
                                                <td className="text-end">{fmtNum(r.stockQty)}</td>
                                                <td className="text-end text-primary">
                                                    {fmtNum(r.recommendedBuy)}
                                                </td>
                                                <td className="text-end fw-medium">{fmtNum(r.orderQtySum)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <h5 className="mt-4 mb-2">Картки крою, яким бракує тканини</h5>
                            {cuttingCardsLoading ? (
                                <Spinner animation="border" />
                            ) : (
                                <CuttingCardsList
                                    cards={cuttingCards}
                                    showShortages
                                    emptyText="Немає карток крою з нестачею тканини."
                                />
                            )}
                        </>
                    )}
                </div>
            )}

            {mainTab === "cut" && (
                <div>
                    <p className="text-muted small mb-3">
                        Картки крою з підтверджених планів. Закройщик може взяти партію в роботу, а потім позначити її
                        як покроєну.
                    </p>
                    {productionCardsLoading ? (
                        <Spinner animation="border" />
                    ) : (
                        <>
                            <h5 className="mb-2">Доступно на крій</h5>
                            <ProductionWorkCardsList
                                cards={productionCardsByStage.ready_for_cut || []}
                                emptyText="Немає партій, доступних на крій."
                                actionLabel="Взяти в роботу"
                                actionFrom="ready_for_cut"
                                actionTo="cut_in_progress"
                                actionName="cut_started"
                                onMove={moveProductionBatch}
                                busyId={productionMoveBusyId}
                            />
                            <h5 className="mt-4 mb-2">У роботі на крої</h5>
                            <ProductionWorkCardsList
                                cards={productionCardsByStage.cut_in_progress || []}
                                emptyText="Немає партій у роботі на крої."
                                actionLabel="Покроєно"
                                actionFrom="cut_in_progress"
                                actionTo="cut_done"
                                actionName="cut_done"
                                onMove={moveProductionBatch}
                                busyId={productionMoveBusyId}
                            />
                        </>
                    )}
                </div>
            )}
            {mainTab === "sew" && (
                <div>
                    <p className="text-muted small mb-3">
                        Пошив працює від покроєних SKU. Швея бере партію в роботу і після завершення переводить її в
                        «пошито».
                    </p>
                    {productionCardsLoading ? (
                        <Spinner animation="border" />
                    ) : (
                        <>
                            <h5 className="mb-2">Доступно швеям</h5>
                            <ProductionWorkCardsList
                                cards={productionCardsByStage.cut_done || []}
                                emptyText="Немає покроєних партій, доступних для пошиву."
                                actionLabel="Взяти в пошив"
                                actionFrom="cut_done"
                                actionTo="sew_in_progress"
                                actionName="sew_started"
                                onMove={moveProductionBatch}
                                busyId={productionMoveBusyId}
                            />
                            <h5 className="mt-4 mb-2">У пошиві</h5>
                            <ProductionWorkCardsList
                                cards={productionCardsByStage.sew_in_progress || []}
                                emptyText="Немає партій у пошиві."
                                actionLabel="Пошито"
                                actionFrom="sew_in_progress"
                                actionTo="sew_done"
                                actionName="sew_done"
                                onMove={moveProductionBatch}
                                busyId={productionMoveBusyId}
                            />
                        </>
                    )}
                </div>
            )}
            {mainTab === "dashboard" && (
                <div>
                    <p className="text-muted small mb-3">
                        Поточний стан виробництва по SKU: план, фактичний склад та кількість на кожному етапі.
                    </p>
                    {productionDashboardLoading ? (
                        <Spinner animation="border" />
                    ) : productionDashboardRows.length === 0 ? (
                        <Alert variant="secondary">Поки немає активних виробничих партій.</Alert>
                    ) : (
                        <div className="table-responsive border rounded">
                            <Table striped hover size="sm" className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>SKU</th>
                                        <th>Товар</th>
                                        <th>Колір</th>
                                        <th>Розмір</th>
                                        <th className="text-end">Склад</th>
                                        <th className="text-end">План крій</th>
                                        <th className="text-end">Матеріали</th>
                                        <th className="text-end">На крій</th>
                                        <th className="text-end">В крої</th>
                                        <th className="text-end">Покроєно</th>
                                        <th className="text-end">В пошиві</th>
                                        <th className="text-end">Пошито</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productionDashboardRows.map((row) => (
                                        <tr key={row.sku}>
                                            <td className="small text-muted">{row.sku}</td>
                                            <td>{row.name}</td>
                                            <td>{row.color}</td>
                                            <td>{row.size}</td>
                                            <td className="text-end">{fmtNum(row.stockQty)}</td>
                                            <td className="text-end">{fmtNum(row.plannedCutQty)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.waiting_materials)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.ready_for_cut)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.cut_in_progress)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.cut_done)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.sew_in_progress)}</td>
                                            <td className="text-end">{fmtNum(row.stages?.sew_done)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </div>
            )}

            <Modal show={showTitleModal} onHide={() => setShowTitleModal(false)} centered>
                <Modal.Header closeButton closeLabel="Закрити вікно">
                    <Modal.Title>Новий план закупівлі</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Label>Назва</Form.Label>
                    <Form.Control
                        value={modalTitle}
                        onChange={(e) => setModalTitle(e.target.value)}
                        placeholder="Наприклад, Тиждень 15–21 квітня"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        title="Закрити без створення нового плану"
                        onClick={() => setShowTitleModal(false)}
                    >
                        Скасувати
                    </Button>
                    <Button
                        variant="primary"
                        disabled={saveLoading}
                        title="Створити план на сервері з поточним прогнозом і вказаною назвою"
                        onClick={() => void handleCreatePlan(modalTitle)}
                    >
                        Створити
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
